/**
 * Numeric tuning for endless highway dodge gameplay (ported from Highway Rush prototype).
 * Centralizes lane geometry, scrolling, and baseline camera placeholders — real camera
 * offsets are finalized in cameraRig.js for the ~60° oblique framing.
 */

/**
 * Lane center X — exactly four lanes across the paved band (`LANE_COUNT` === 4).
 * Edges and dashed dividers are derived below so striping always matches gameplay snaps.
 */
export const LANE_X = [-2.625, -0.875, 0.875, 2.625];

/** Center-to-center spacing between adjacent `LANE_X` entries (uniform four-lane grid). */
const LANE_CENTER_SPACING = LANE_X[1] - LANE_X[0];

/** Playable lane count — must equal `LANE_X.length` (four lanes max for this mode). */
export const LANE_COUNT = LANE_X.length;

/**
 * World X of dashed lines **between** lane centers (three internal dividers for four lanes).
 */
export const LANE_DIVIDER_X = LANE_X.slice(0, -1).map((x, i) => (x + LANE_X[i + 1]) * 0.5);

/**
 * Outer edge X of the two shoulder-side lanes — white edge striping sits here.
 */
export const LANE_EDGE_X = [
  LANE_X[0] - LANE_CENTER_SPACING * 0.5,
  LANE_X[LANE_COUNT - 1] + LANE_CENTER_SPACING * 0.5
];

/**
 * Half-width of the asphalt slab: outer edge matches the outer lane lines (`LANE_EDGE_X`).
 * No extra pavement past the fourth lane — corridor width equals the four-lane grid only.
 */
export const ROAD_SURFACE_HALF = Math.max(
  Math.abs(LANE_EDGE_X[0]),
  Math.abs(LANE_EDGE_X[1])
);

/** Full paved width (lane-1 outer edge → lane-4 outer edge). */
export const ROAD_WIDTH = ROAD_SURFACE_HALF * 2;

/**
 * Optional shoulder outside asphalt. Kept at `0` so guardrails sit flush with the fourth-lane edge.
 */
export const SHOULDER_WIDTH = 0;

/**
 * |X| of guard posts/beam — with zero shoulders, matches {@link ROAD_SURFACE_HALF} (lane outer edge).
 */
export const GUARD_LINE_ABS_X = ROAD_SURFACE_HALF + SHOULDER_WIDTH;

/** Trees / rocks: just outside the guard line, closer to the road edge. */
export const PROP_MIN_ABS_X = 4.8;
export const PROP_MAX_ABS_X = 7.5;

/** Building centers — tighter band toward the guard so blocks hug the corridor border (width ≤ ~7). */
export const BUILDING_MIN_ABS_X = 7;
export const BUILDING_MAX_ABS_X = 11.5;

/** Labels for scenery recycle bands in endlessRacerGame. */
export const SCENERY_KIND_BUILDING = "building";
export const SCENERY_KIND_PROP = "prop";

/**
 * @param {number} minAbs
 * @param {number} maxAbs
 * @returns {number} random X on either side of the road (signed).
 */
export function randomSignedAbsX(minAbs, maxAbs) {
  const side = Math.random() > 0.5 ? 1 : -1;
  return side * (minAbs + Math.random() * (maxAbs - minAbs));
}

/** World Z — pulled toward the lens on phones with portrait camera defaults so the cab isn’t a hairline. */
export const PLAYER_FIXED_Z = 15.1;

/**
 * Baseline yaw for the player rig (radians). `Math.PI` turns the cab 180° so the nose points toward
 * the camera / HUD; keep `0` for classic “into the road” facing. X‑long OBJ exports still add π/2 in
 * `normalizeLoadedPlayerTruck`.
 */
export const PLAYER_MESH_YAW = Math.PI;

/** Shrinks AI props vs reference rig so the OBJ reads dominant. */
export const TRAFFIC_VISUAL_SCALE = 0.72;

/** Player nominal height above deck (used if mesh has no grounding pass). */
export const PLAYER_DEFAULT_Y = 0.35;

/** Max simulated cruising speed units/sec early run — slightly higher so the open feels less tame. */
export const SPEED_CAP = 82;

/** Speed ceiling once difficulty ramps — longer runs mean faster scroll & denser weave windows. */
export const SPEED_CAP_PEAK = 94;

/** Seconds of survival before difficulty blend hits 100% — shorter = threatening pace kicks in sooner. */
export const DIFFICULTY_RAMP_SEC = 88;

/**
 * Seconds for `crashRelief` to linearly drain from 1 → 0 after a collision (spawn-stress trim in psychDifficulty).
 * Tweak with playtests: shorter = snappier return to peak density; longer = more post-crash breathing room.
 */
export const ENGAGEMENT_RELAX_TAU_SEC = 10.5;

/** Congestion floor / ceiling — busier road from second one (`MAX_TRAFFIC_START` lifts day-one pressure). */
export const MAX_TRAFFIC_START = 8;
export const MAX_TRAFFIC_PEAK = 12;
export const SPEED_INITIAL = 38;

/** Idle speed clamp after crashing. */
export const SPEED_AFTER_CRASH_FLOOR = 26;

/** One-and-done run — single crash ends the round when this is `1`. */
export const STARTING_LIVES = 1;
